/* eslint-disable @nx/enforce-module-boundaries */
import {
  AskTestimonialIcon,
  BlockIcon,
  ReportIcon,
  SharedConnectionIcon,
  File,
  Follow,
  MeetNow,
  ScheduleIcon,
  RemoveHandshake,
  Withdraw,
  WithdrawHandshake,
  HandShakeSimpleIcon,
} from '@mybridge/icons';
import {
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
} from '@mybridge/ui';
import { useRouter } from 'next/router';
import { MatrixContext } from 'packages/mybridgev4/context/chat-context';
import { forwardRef, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { ForceSignInWrapper } from 'v4/components/force-sign-in-wrapper';
import InnerMenu from 'v4/components/multilevelmenu/InnerMenu';
import MenuItemSubMenu from 'v4/components/multilevelmenu/MenuItemSubMenu';
import { MyNetworkContext } from 'v4/components/network/context';
import { PublicProfileContext } from 'v4/components/public-profile/context';
import { getUserFullName } from 'v4/lib/commons';
import { scheduleMeetingWithUser } from 'v4/store/actions/meeting.actions';
import { getUserInfoPDF } from 'v4/store/actions/user-profile.actions';
import { MyProfileContext } from 'v4/components/profile/context';
import { MyProfileMyBioDropdown } from 'v4/snippets/profile/my-bio-dropdown';
import { Stack, useToast } from '@chakra-ui/react';
import { MeetingDefaultIcon } from '@mybridge/icons/NotificationIcons';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';

export const PublicProfileMenu = forwardRef(({ children, ...props }, ref) => {
  const dispatch = useDispatch();
  const { publicProfile } = useContext(PublicProfileContext);
  const {
    setSelectedUser,
    reportUserDisc,
    blockUserDisc,
    unfollowUser,
    isFollowed,
    followUser,
    isFriend,
    isHandshaked,
    handleHandshakeSendRevoke,
    handshakeUser,
    unfriendUser,
    setLoadingPDF
  } = useContext(MyNetworkContext);
 const router=useRouter()
 const toast=useToast();
 const { t } = useTranslator();

 const requestProfilePDF = async () => {
  try {
    setLoadingPDF(true);

    const res = await dispatch(getUserInfoPDF(publicProfile?.slug));
    const htmlContent = res?.payload;

    if (!htmlContent) {
      toast({
        title: "Error",
        description: "Failed to get profile HTML.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const userName = getUserFullName(publicProfile) || "Profile";
    const safeFileName = userName
      .replace(/[^a-z0-9_\- ]/gi, "")
      .trim();

    const PRINT_STYLES = `
      <style id="__pdf_print_styles__">
        @page {
          size: A4;
          margin: 0px;
        }

        * {
          box-sizing: border-box;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        html,
        body {
          margin: 0;
          padding: 0;
          background: #ffffff;
          width: 100%;
        }

        img {
          max-width: 100%;
        }

        section,
        .page-section,
        .about-us-section,
        .skills-container,
        .experience-block-section,
        .profile-details-block,
        .skill-item {
          break-inside: avoid;
          page-break-inside: avoid;
        }

        h1,
        h2,
        h3,
        h4,
        h5,
        h6,
        .section-heading {
          break-after: avoid;
          page-break-after: avoid;
          page-break-inside: avoid;
        }

        h3.section-heading + * {
          break-before: avoid;
          page-break-before: avoid;
        }

        .page-section {
          margin-bottom: 14px;
        }

        .experience-block-section {
          margin-bottom: 12px;
        }

        .skill-item {
          margin-bottom: 10px;
        }

        img,
        svg {
          break-inside: avoid;
          page-break-inside: avoid;
        }

        body {
          -webkit-font-smoothing: antialiased;
          text-rendering: optimizeLegibility;
        }
      </style>
    `;

      const finalHTML = htmlContent.includes("</head>")
        ? htmlContent.replace("</head>", `${PRINT_STYLES}</head>`)
        : `${PRINT_STYLES}${htmlContent}`;

      const response = await fetch("/api/generate-pdf", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          html: finalHTML,
          fileName: `${safeFileName}_profile`,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(errorText);
        throw new Error("Failed to generate PDF");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeFileName}_profile.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("PDF generation error:", error);

      toast({
        title: "Error",
        description: "Failed to generate PDF.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingPDF(false);
    }
  };
  
  const { startMeetingSingle, startChatSingle } = useContext(MatrixContext);

  const handleStartChatClick = (e) => {
    router.push(`/profile/message?user=${publicProfile?.matrix_user_id}`)
    // startChatSingle(publicProfile);
  };

  const handleMeetNowClick = (e) => {
    startMeetingSingle(publicProfile);
  };

  // Helper function to handle unfriend action
  const handleUnfriend = (e) => {
    unfriendUser(publicProfile);
  };

  // Determine connection status and appropriate action
  const getConnectionAction = () => {
    if (isFriend?.(publicProfile)) {
      return {
        title: 'Unfriend',
        icon: <WithdrawHandshake width="18" height="18" color="#868585" />,
        onClick: handleUnfriend
      };
    } else if (isHandshaked(publicProfile)) {
      return {
        title: t('publicProfile.withdrawHandshake') || 'Withdraw Handshake',
        icon: <WithdrawHandshake width="18" height="18" />,
        onClick: publicProfile?.is_blocked ? undefined : (e) => handleHandshakeSendRevoke(publicProfile),
        isDisabled: publicProfile?.is_blocked
      };
    } else {
      return {
        title: t('publicProfile.handshake') || 'Handshake',
        icon: <HandShakeSimpleIcon width="18" height="18" color="rgb(148, 176, 150)" />,
        onClick: publicProfile?.is_blocked ? undefined : (e) => handshakeUser(publicProfile),
        isDisabled: publicProfile?.is_blocked
      };
    }
  };

  const menu = !isFriend?.(publicProfile)
    ? [
        {
          title: t('publicProfile.sharedConnections') || 'Shared Connections',
          icon: <SharedConnectionIcon width="18" height="18" color="#868585" />,
          link: `/profile/network/${publicProfile?.slug}`,
        },
        {
          title: isFollowed(publicProfile) ? t('publicProfile.unfollow') || 'Unfollow' : t('publicProfile.follow') || 'Follow',
          icon: <Follow width="20" height="20" color="#868585" />,
          onClick: isFollowed(publicProfile)
            ? (e) => unfollowUser(publicProfile)
            : (e) => followUser(publicProfile),
        },
        {
          title: `${t('publicProfile.print') || "Print"} ${getUserFullName(publicProfile)}'s ${t('publicProfile.profile') || "Profile"}`,
          icon: <File width="20" height="20" color="#868585" />,
          onClick: (e) => {
            requestProfilePDF();
          },
          isPrintProfile: true,
        },
        {
          title: t('profilePhotosVideos.report') || 'Report',
          icon: <ReportIcon width="18" height="18" color="#868585" />,
          onClick: (e) => {
            setSelectedUser(publicProfile);
            reportUserDisc?.onOpen?.();
          },
        },
        getConnectionAction(),
      ] : [
        {
          title: t('publicProfile.message') || 'Message',
          icon: <AskTestimonialIcon width="20" height="20" color="#868585" />,
          onClick:()=>{
            handleStartChatClick()
          }
        },
        {
          title: t('publicProfile.meetNow') || 'Meet Now',
          icon: <MeetingDefaultIcon width="18" height="18" color="#868585" />,
            onClick:()=>{
              handleMeetNowClick()
          }
        },
        {
          title: t('publicProfile.schedule') || 'Schedule',
          icon: <ScheduleIcon width="20" height="20" color="#868585" />,
          onClick:()=>{
            dispatch(
              scheduleMeetingWithUser({ users: publicProfile, type: 'connection' })
            );
            router.push('/profile/calendar/schedule');
          }
        },
        {
          title: t('publicProfile.sharedConnections') || 'Shared Connections',
          icon: <SharedConnectionIcon width="18" height="18" color="#868585" />,
          link: `/profile/network/${publicProfile?.slug}`,
        },
        {
          title: 'Unfriend',
          icon: <WithdrawHandshake width="18" height="18" color="#868585" />,
          onClick: handleUnfriend,
        },
        {
          title: `${t('publicProfile.print') || "Print"} ${getUserFullName(publicProfile)}'s ${t('publicProfile.profile') || "Profile"}`,
          icon: <File width="20" height="20" color="#868585" />,
          onClick: (e) => {
            requestProfilePDF();
          },
          isPrintProfile: true,
        },        
        {
          title: t('publicProfile.block') || 'Block',
          icon: <BlockIcon width="20" height="20" color="#868585" />,
          onClick: () => {
            setSelectedUser(publicProfile);
            blockUserDisc?.onOpen?.();
          },
        },
        {
          title: t('profilePhotosVideos.report') || 'Report',
          icon: <ReportIcon width="18" height="18" color="#868585" />,
          onClick: () => {
            setSelectedUser(publicProfile);
            reportUserDisc?.onOpen?.();
          },
        },
      ];

  return (
    <>
      <Menu>
        <MenuButton {...props}>{children}</MenuButton>
        <Portal>
          <MenuList pos="relative" zIndex="8">
            {menu?.map(
              ({ link, icon, title, isMultiLevel, isPrintProfile, ...props }, miIndex) => (
                <ForceSignInWrapper key={miIndex}>
                  {isMultiLevel ? (
                    <MenuItemSubMenu>
                      <InnerMenu
                        title="Movies"
                        childrenItems={[
                          'New Release Movies  ',
                            'Disney Movies  ',
                            'TV Shows  ',
                            'YouTube Video  ',
                          'New Release Movies',
                          'Disney Movies',
                          'TV Shows',
                          'YouTube Video',
                        ]}
                      />
                    </MenuItemSubMenu>
                  ) : (
                    <>
                      <MenuItem
                        color="#868585"
                        {...(link ? { as: Link, href: link } : {})}
                        icon={icon}
                        key={miIndex}    
                        {...props}
                      >
                        {title}
                      </MenuItem>
                      {isPrintProfile && (
                        <Stack display={"flex"}>
                          <MyProfileMyBioDropdown pl="12px" userName={getUserFullName(publicProfile)} jobPreferences={publicProfile?.job_preference} />
                        </Stack>
                      )}
                    </>
                  )}
                </ForceSignInWrapper>
              )
            )}
          </MenuList>
        </Portal>
      </Menu>
    </>
  );
});
